import {
  ArrowLeftRight,
  ArrowUp,
  ClipboardList,
  Calculator,
  Edit2,
  History,
  Info,
  Pencil,
  RefreshCw,
  RotateCcw,
  Store,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import type { components } from "@/api/schema";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import type { RowAction } from "@/shared/components/RowActionMenu";
import { RowActionMenu } from "@/shared/components/RowActionMenu";
import { ROW_HEIGHT, type Density } from "@/shared/hooks/useDensity";
import { toast } from "@/shared/lib/toast";
import { Tooltip } from "@/shared/ui/tooltip";

import { useCalculateReset } from "../api/use-calculate-reset";
import { useRecalculate } from "../api/use-recalculate";
import { useRecomputeListing } from "../api/use-recompute-listing";
import { useSetListingMultiplier } from "../api/use-set-listing-multiplier";
import { useSyncMulti } from "../api/use-sync-multi";
import { useSyncStock } from "../api/use-sync-stock";
import { useUpdateStock } from "../api/use-update-stock";
import { formatMultiplier, parseMultiplier } from "../lib/format-multiplier";
import { formatSyncTime, relativeSyncTime } from "../lib/format-sync-time";
import { lastSyncedState } from "../lib/last-synced";
import { parseRowKey } from "../lib/row-key";
import { thumbGradient, thumbInitial } from "../lib/thumbnail";
import type { SortDir, SortKey } from "../lib/url-state";

import { AllocationCell } from "./AllocationCell";
import { BulkEditModal } from "./BulkEditModal";
import { EmptyStates } from "./EmptyStates";
import { InlineEditCell } from "./InlineEditCell";
import { ResetRowConfirm } from "./ResetRowConfirm";
import { StockStatusBadge } from "./StockStatusBadge";

type Stock = components["schemas"]["Stock"];
type ListingOwnStock = components["schemas"]["ListingOwnStock"];
type Marketplace = components["parameters"]["Marketplace"];

/**
 * Rows in the unified inventory grid are a discriminated union: base stock SKUs
 * (`Stock`, the default — no `type` needed so existing callers pass `Stock[]`
 * unchanged) and bundle (listing) rows exploded one-per-marketplace from a
 * listing's `own_stock` array (Stage 2). Bundle rows carry only the columns a
 * listing actually owns; the rest render "—".
 */
export type InventoryBaseRow = Stock & { type?: "base" };
export interface InventoryBundleRow {
  type: "bundle";
  listing_code: string;
  product_name?: string;
  /** One `own_stock` entry — its own reserve/sellable/allocation_sellable/multiplier for one marketplace. */
  own: ListingOwnStock;
}
export type InventoryRow = InventoryBaseRow | InventoryBundleRow;

interface InventoryTableProps {
  items: InventoryRow[];
  isLoading: boolean;
  isError?: boolean;
  onDeleteSku?: (sku: string) => void;
  onEditSku?: (sku: string, marketplace: string) => void;
  onTransfer?: (sku: string, marketplace: string) => void;
  onAudit?: (sku: string, marketplace: string) => void;
  onCheckSellername?: (sku: string, marketplace: string) => void;
  onPushHistory?: (sku: string, marketplace: string) => void;
  /** Bundle-row actions (only wired when the table is fed bundle rows). */
  onEditBundle?: (code: string) => void;
  onDeleteBundle?: (code: string) => void;
  density?: Density;
  onClearFilters?: () => void;
  onRetry?: () => void;
  sortKey?: SortKey;
  sortDir?: SortDir;
  onSort?: (key: SortKey) => void;
  /** Overrides the table's `data-testid` so a bundle-fed instance can coexist
   *  with the base instance in the "All" view without a testid collision. */
  tableTestId?: string;
  /** stock:write — selection column, bulk bar, inline stock edits, stock row write actions. Default true. */
  canWriteStock?: boolean;
  /** listing:write — bundle inline multiplier and bundle row write actions. Default true. */
  canWriteListing?: boolean;
}

const STICKY_BASE = "sticky bg-[var(--row-bg,#fff)] z-10 border-r border-[var(--border-subtle)]";

function Th({
  children,
  sticky,
  left,
  width,
  align = "left",
  className = "",
  heightClass = "h-9",
  ariaSort,
}: {
  children: ReactNode;
  sticky?: boolean;
  left?: number;
  width?: number;
  align?: "left" | "right";
  className?: string;
  heightClass?: string;
  ariaSort?: "ascending" | "descending" | "none";
}) {
  return (
    <th
      aria-sort={ariaSort}
      className={[
        `${heightClass} px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap select-none`,
        sticky ? STICKY_BASE : "",
        align === "right" ? "text-right" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        left: sticky && left !== undefined ? `${left}px` : undefined,
        width: width ? `${width}px` : undefined,
        minWidth: width ? `${width}px` : undefined,
      }}
    >
      {children}
    </th>
  );
}

/**
 * A sortable column header: a clickable button inside a `<Th>` that toggles the
 * sort and reflects the current sort state via `aria-sort` (on the `<th>`, the
 * semantically correct host) plus a rotating chevron. The accessible name is
 * `t("sort.sortBy", { column })` so the integration test can target it.
 */
function SortableTh({
  columnKey,
  sortKey,
  sortDir,
  onSort,
  align = "left",
  className = "",
  heightClass = "h-9",
  sticky,
  left,
  width,
  children,
}: {
  columnKey: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort?: ((key: SortKey) => void) | undefined;
  align?: "left" | "right";
  className?: string;
  heightClass?: string;
  sticky?: boolean;
  left?: number;
  width?: number;
  children: ReactNode;
}) {
  const { t } = useTranslation("inventory");
  const active = sortKey === columnKey;
  const ariaSort = active ? (sortDir === "asc" ? "ascending" : "descending") : "none";
  // No sort handler (e.g. the bundle-fed table) → render a plain, non-interactive
  // header. This also prevents duplicate "Sort by …" buttons in the "All" view,
  // where a base table and a bundle table render their headers side by side.
  if (!onSort) {
    return (
      <Th
        align={align}
        className={className}
        heightClass={heightClass}
        {...(sticky ? { sticky } : {})}
        {...(left !== undefined ? { left } : {})}
        {...(width !== undefined ? { width } : {})}
      >
        {children}
      </Th>
    );
  }
  return (
    <Th
      align={align}
      className={className}
      heightClass={heightClass}
      ariaSort={ariaSort}
      {...(sticky ? { sticky } : {})}
      {...(left !== undefined ? { left } : {})}
      {...(width !== undefined ? { width } : {})}
    >
      <button
        type="button"
        aria-label={t("sort.sortBy", { column: String(children) })}
        onClick={() => onSort?.(columnKey)}
        className={[
          "inline-flex items-center gap-1 focus-ring rounded select-none",
          align === "right" ? "flex-row-reverse w-full justify-start" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span>{children}</span>
        <ArrowUp
          className={[
            "h-3 w-3 transition-transform",
            active ? "text-[var(--text-default)]" : "text-[var(--text-subtle)] opacity-40",
            active && sortDir === "desc" ? "rotate-180" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden
        />
      </button>
    </Th>
  );
}

function Td({
  children,
  sticky,
  left,
  width,
  align = "left",
  className = "",
  heightClass = "h-9",
  "data-testid": dataTestid,
}: {
  children?: ReactNode;
  sticky?: boolean;
  left?: number;
  width?: number;
  align?: "left" | "right";
  className?: string;
  heightClass?: string;
  "data-testid"?: string;
}) {
  return (
    <td
      data-testid={dataTestid}
      className={[
        `${heightClass} px-4 text-sm text-[var(--text-primary)] tabular-nums whitespace-nowrap`,
        sticky ? STICKY_BASE : "",
        align === "right" ? "text-right" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        left: sticky && left !== undefined ? `${left}px` : undefined,
        width: width ? `${width}px` : undefined,
        minWidth: width ? `${width}px` : undefined,
      }}
    >
      {children}
    </td>
  );
}

/**
 * Last-synced cell text (`lib/last-synced.ts`): the list endpoint carries no
 * per-row push log, so only `never`/`ok`/`pending` are actually reachable
 * here — `failed`/`no_listing` need a log entry (only available from the
 * Push history drawer), per the function's own doc comment.
 */
function SyncedCell({ row }: { row: Stock }) {
  const { t, i18n } = useTranslation("inventory");
  const state = lastSyncedState(row);
  if (state.kind === "never") {
    return <>{t("synced.never")}</>;
  }
  if (state.kind === "pending") {
    return (
      <span
        className="font-semibold text-[var(--intent-warning-fg)]"
        title={formatSyncTime(row.last_synced_at)}
      >
        {t("synced.pending", { rel: relativeSyncTime(state.rel, i18n.language) })}
      </span>
    );
  }
  if (state.kind === "failed") {
    return (
      <span className="font-semibold text-[var(--intent-danger-fg)]" title={state.error}>
        {t("synced.failed", { rel: relativeSyncTime(state.rel, i18n.language) })}
      </span>
    );
  }
  if (state.kind === "no_listing") {
    return <>{t("synced.no_listing")}</>;
  }
  return (
    <span title={formatSyncTime(row.last_synced_at)}>
      {relativeSyncTime(state.rel, i18n.language)}
    </span>
  );
}

export function InventoryTable({
  items,
  isLoading,
  isError = false,
  onDeleteSku,
  onEditSku,
  onTransfer,
  onAudit,
  onCheckSellername,
  onPushHistory,
  onEditBundle,
  onDeleteBundle,
  density,
  onClearFilters,
  onRetry,
  sortKey,
  sortDir,
  onSort,
  tableTestId = "inventory-table",
  canWriteStock = true,
  canWriteListing = true,
}: InventoryTableProps): JSX.Element {
  const { t } = useTranslation(["inventory", "common"]);
  // The SKU column sticks right after the selection column; with no selection
  // column (read-only) it starts at the left edge.
  const skuLeft = canWriteStock ? 48 : 0;
  const { mutate: updateStock } = useUpdateStock();
  const { mutate: resetDefaults, isPending: isResetting } = useCalculateReset();
  const { mutate: syncSelected, isPending: isSyncing } = useSyncMulti();
  const { mutate: recalculate } = useRecalculate();
  const { mutate: recomputeListing } = useRecomputeListing();
  const { mutate: setListingMultiplier } = useSetListingMultiplier();
  const { mutate: syncStock } = useSyncStock();
  const rowHeight = ROW_HEIGHT[density ?? "default"];
  const dash = <span className="text-[var(--text-subtle)]">{t("common:placeholder.empty")}</span>;

  // Client-side debounce for per-row Sync: a (sku, marketplace) key is held in
  // this set while its push is in flight so double-clicks don't spam the marketplace
  // (the server also rate-limits, but be a good citizen). Disables the menu item.
  const [syncing, setSyncing] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<{ sku: string; marketplace: Marketplace } | null>(
    null,
  );
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const rowKey = (r: Stock): string => `${r.sku}-${r.marketplace}`;
  // Only base rows participate in selection/bulk actions; bundle rows do not.
  const baseItems = items.filter((r): r is InventoryBaseRow => r.type !== "bundle");
  const rowKeys = baseItems.map(rowKey);
  const allSelected = rowKeys.length > 0 && rowKeys.every((k) => selected.has(k));
  const someSelected = rowKeys.some((k) => selected.has(k));

  useEffect(() => {
    if (headerCheckRef.current) headerCheckRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  function toggleAll(): void {
    setSelected(allSelected ? new Set() : new Set(rowKeys));
  }

  function toggleRow(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleMultiplierSave(sku: string, mkp: string) {
    return (rawValue: string, remark?: string) => {
      const parsed = parseMultiplier(rawValue);
      if (parsed.ok) {
        updateStock({
          sku,
          mkp,
          body: { multiplier: parsed.value, ...(remark ? { remark } : {}) },
        });
      }
    };
  }

  function handleListingMultiplierSave(code: string, mkp: Marketplace) {
    return (rawValue: string) => {
      const parsed = parseMultiplier(rawValue);
      if (parsed.ok) {
        setListingMultiplier({ code, marketplace: mkp, multiplier: parsed.value });
      }
    };
  }

  function handleFakeSave(sku: string, mkp: string) {
    return (rawValue: string, remark?: string) => {
      const n = parseInt(rawValue, 10);
      if (Number.isFinite(n) && n >= 0) {
        updateStock({ sku, mkp, body: { fake: n, ...(remark ? { remark } : {}) } });
      }
    };
  }

  function handleLockSave(sku: string, mkp: string) {
    return (rawValue: string, remark?: string) => {
      const n = parseInt(rawValue, 10);
      if (Number.isFinite(n) && n >= 0) {
        updateStock({ sku, mkp, body: { lock: n, ...(remark ? { remark } : {}) } });
      }
    };
  }

  // "Calculate" row action (PRD v1.8 §4.6.3.2-B): non-destructive recompute of
  // the derived columns for this row. NEVER changes multiplier/fake/lock.
  function handleRecalculate(sku: string, marketplace: Marketplace): void {
    recalculate([{ sku, marketplace }], {
      onSuccess: () => toast.success(t("action.calculate_done", { sku })),
      onError: () => toast.error(t("action.calculate_error")),
    });
  }

  // Per-row on-demand Sync (PRD v1.8 §4.6.3.2-C): push this row's
  // server-computed allocation_sellable to the marketplace. Rate-limited server-side
  // (HTTP 429 → "try again shortly"); also debounced client-side via `syncing`.
  function handleSync(sku: string, marketplace: Marketplace): void {
    const key = `${sku}-${marketplace}`;
    if (syncing.has(key)) return;
    setSyncing((prev) => new Set(prev).add(key));
    syncStock(
      { sku, marketplace },
      {
        onSuccess: (data) => {
          const item = data.items[0];
          if (item && !item.ok) {
            toast.error(t("sync.toast_failed", { sku, message: item.message }));
          } else {
            toast.success(t("sync.toast_done", { sku, mkp: marketplace }));
          }
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 429) {
            toast.warn(t("sync.rate_limited"));
          } else if (err instanceof ApiError && err.status === 501) {
            // Permanent until the direct-marketplace push is wired — a retry
            // can never succeed, so don't tell the user to try again.
            toast.warn(t("sync.not_available"));
          } else {
            toast.error(t("sync.toast_error", { sku }));
          }
        },
        onSettled: () => {
          setSyncing((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        },
      },
    );
  }

  // Bundle-row "Recompute" (Stage 2): mirrors sino-legacy's Calculate button.
  // Forces a sellable/allocation_sellable recompute for one listing+marketplace.
  function handleRecomputeListing(code: string, marketplace: Marketplace): void {
    recomputeListing(
      { code, marketplace },
      {
        onSuccess: () => toast.success(t("bundle.toast.recomputed", { code, mkp: marketplace })),
        onError: () => toast.error(t("bundle.toast.recompute_error", { code })),
      },
    );
  }

  if (isError) return <EmptyStates variant="error" {...(onRetry && { onRetry })} />;
  if (!isLoading && items.length === 0)
    return <EmptyStates variant="no-results" {...(onClearFilters && { onClearFilters })} />;

  return (
    <>
      <div
        className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)]"
        style={{ boxShadow: "var(--sh-card)" }}
      >
        <table
          className="min-w-full border-collapse"
          data-testid={tableTestId}
          data-density={density ?? "default"}
        >
          <thead className="sticky top-0 z-20 bg-white border-b border-[var(--row-divider)]">
            <tr>
              {canWriteStock && (
                <Th sticky left={0} width={48} heightClass={rowHeight}>
                  <input
                    ref={headerCheckRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer rounded"
                    style={{ accentColor: "var(--brand-500)" }}
                    aria-label="Select all"
                  />
                </Th>
              )}
              <SortableTh
                columnKey="sku"
                sortKey={sortKey ?? "sku"}
                sortDir={sortDir ?? "asc"}
                onSort={onSort}
                sticky
                left={skuLeft}
                width={260}
                heightClass={rowHeight}
              >
                {t("column.product")}
              </SortableTh>
              <Th sticky left={skuLeft + 260} width={132} heightClass={rowHeight}>
                {t("column.marketplace")}
              </Th>
              <Th heightClass={rowHeight}>{t("column.type")}</Th>
              <SortableTh
                columnKey="on_hand"
                sortKey={sortKey ?? "sku"}
                sortDir={sortDir ?? "asc"}
                onSort={onSort}
                align="right"
                heightClass={rowHeight}
              >
                {t("column.on_hand")}
              </SortableTh>
              <Th align="right" heightClass={rowHeight}>
                {t("column.fake")}
              </Th>
              <Th align="right" heightClass={rowHeight}>
                <span>{t("column.lock")}</span>
                <Tooltip content={t("tooltip.lock")}>
                  <button
                    type="button"
                    aria-label={t("tooltip.lock")}
                    className="ml-1 inline-flex cursor-help text-[var(--text-subtle)] focus-ring rounded"
                  >
                    <Info className="h-3 w-3" aria-hidden />
                  </button>
                </Tooltip>
              </Th>
              <Th align="right" heightClass={rowHeight}>
                {t("column.reserved")}
              </Th>
              <Th align="right" heightClass={rowHeight}>
                {t("column.bundle_reserve")}
              </Th>
              <Th align="right" heightClass={rowHeight}>
                {t("column.multiplier")}
              </Th>
              <SortableTh
                columnKey="sellable"
                sortKey={sortKey ?? "sku"}
                sortDir={sortDir ?? "asc"}
                onSort={onSort}
                align="right"
                className="bg-[var(--surface-spotlight)] border-l border-[var(--brand-100)]"
                heightClass={rowHeight}
              >
                {t("column.sellable")}
              </SortableTh>
              <SortableTh
                columnKey="allocation_sellable"
                sortKey={sortKey ?? "sku"}
                sortDir={sortDir ?? "asc"}
                onSort={onSort}
                align="right"
                className="bg-[var(--surface-spotlight)]"
                heightClass={rowHeight}
              >
                {t("column.allocation")}
              </SortableTh>
              <SortableTh
                columnKey="status_code"
                sortKey={sortKey ?? "sku"}
                sortDir={sortDir ?? "asc"}
                onSort={onSort}
                className="bg-[var(--surface-spotlight)]"
                heightClass={rowHeight}
              >
                {t("column.status")}
              </SortableTh>
              <SortableTh
                columnKey="last_synced_at"
                sortKey={sortKey ?? "sku"}
                sortDir={sortDir ?? "asc"}
                onSort={onSort}
                heightClass={rowHeight}
              >
                {t("column.last_synced")}
              </SortableTh>
              <Th
                className="sticky right-0 z-[6] bg-white shadow-[-10px_0_12px_-10px_rgba(16,24,40,0.16)]"
                heightClass={rowHeight}
              >
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--row-divider)]">
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {canWriteStock && (
                      <Td sticky left={0} width={48} heightClass={rowHeight}>
                        <div className="h-4 w-4 rounded bg-[var(--surface-sunken)]" />
                      </Td>
                    )}
                    <Td sticky left={skuLeft} width={260} heightClass={rowHeight}>
                      <div className="h-3 w-32 rounded bg-[var(--surface-sunken)]" />
                    </Td>
                    <Td sticky left={skuLeft + 260} width={132} heightClass={rowHeight}>
                      <div className="h-3 w-20 rounded bg-[var(--surface-sunken)]" />
                    </Td>
                    {Array.from({ length: 12 }).map((__, j) => (
                      <Td key={j} heightClass={rowHeight}>
                        <div className="h-3 w-12 rounded bg-[var(--surface-sunken)]" />
                      </Td>
                    ))}
                    <td
                      className={`${rowHeight} sticky right-0 z-[5] bg-[var(--surface-raised,white)] px-2 shadow-[-10px_0_12px_-10px_rgba(16,24,40,0.16)]`}
                    >
                      <div className="h-4 w-4 rounded bg-[var(--surface-sunken)]" />
                    </td>
                  </tr>
                ))
              : items.map((row, index) => {
                  if (row.type === "bundle") {
                    const code = row.listing_code;
                    const mkp = (row.own.marketplace ?? "") as Marketplace;
                    const testKey = `${code}-${mkp}`;
                    const bundleActions = [
                      ...(canWriteListing
                        ? [
                            {
                              key: "recompute",
                              label: t("bundle.action.recompute"),
                              icon: <Calculator className="h-4 w-4" />,
                              onSelect: () => handleRecomputeListing(code, mkp),
                            },
                          ]
                        : []),
                      ...(onEditBundle && canWriteListing
                        ? [
                            {
                              key: "edit",
                              label: t("bundle.action.edit"),
                              icon: <Pencil className="h-4 w-4" />,
                              onSelect: () => onEditBundle(code),
                            },
                          ]
                        : []),
                      ...(onDeleteBundle && canWriteListing
                        ? [
                            {
                              key: "delete",
                              label: t("bundle.action.delete"),
                              danger: true,
                              onSelect: () => onDeleteBundle(code),
                            },
                          ]
                        : []),
                    ] satisfies RowAction[];
                    return (
                      <tr
                        key={`bundle-${testKey}`}
                        className="row-enter group transition-colors hover:bg-[var(--row-hover)]"
                        style={{ animationDelay: `${Math.min(index, 10) * 30}ms` } as CSSProperties}
                        data-testid="bundles-row"
                      >
                        {canWriteStock && <Td sticky left={0} width={48} heightClass={rowHeight} />}
                        <Td sticky left={skuLeft} width={260} heightClass={rowHeight}>
                          <div className="flex items-center gap-3">
                            <span
                              className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] text-xs font-bold text-white"
                              style={{ background: thumbGradient(code), boxShadow: "var(--sh-xs)" }}
                            >
                              {thumbInitial(row.product_name ?? code)}
                            </span>
                            <span className="min-w-0">
                              <span
                                className="block max-w-[210px] truncate text-[13.5px] font-semibold text-[var(--text-primary)]"
                                title={row.product_name ?? code}
                              >
                                {row.product_name ?? code}
                              </span>
                              <span className="sku block text-[11.5px] text-[var(--text-subtle)]">
                                {code}
                              </span>
                            </span>
                          </div>
                        </Td>
                        <Td sticky left={skuLeft + 260} width={132} heightClass={rowHeight}>
                          {mkp ? <MarketplaceChip marketplace={mkp} /> : dash}
                        </Td>
                        <Td heightClass={rowHeight} data-testid={`bundlerow-type-${testKey}`}>
                          {t("type.bundle")}
                        </Td>
                        <Td
                          align="right"
                          heightClass={rowHeight}
                          data-testid={`bundlerow-onhand-${testKey}`}
                        >
                          {dash}
                        </Td>
                        <Td
                          align="right"
                          heightClass={rowHeight}
                          data-testid={`bundlerow-fake-${testKey}`}
                        >
                          {dash}
                        </Td>
                        <Td
                          align="right"
                          heightClass={rowHeight}
                          data-testid={`bundlerow-lock-${testKey}`}
                        >
                          {dash}
                        </Td>
                        <Td
                          align="right"
                          heightClass={rowHeight}
                          data-testid={`bundlerow-reserve-${testKey}`}
                        >
                          <span className="num">{(row.own.reserve ?? 0).toLocaleString()}</span>
                        </Td>
                        <Td
                          align="right"
                          heightClass={rowHeight}
                          data-testid={`bundlerow-bundlereserve-${testKey}`}
                        >
                          {dash}
                        </Td>
                        <Td
                          align="right"
                          heightClass={rowHeight}
                          data-testid={`bundlerow-multiplier-${testKey}`}
                        >
                          <InlineEditCell
                            value={String(row.own.multiplier ?? 1)}
                            displayValue={formatMultiplier(String(row.own.multiplier ?? 1))}
                            onSave={handleListingMultiplierSave(code, mkp)}
                            validate={(v) => {
                              const r = parseMultiplier(v);
                              return r.ok ? null : r.error;
                            }}
                            className="text-right"
                            readOnly={!canWriteListing}
                            data-testid={`inline-listing-multiplier-${code}-${mkp}`}
                          />
                        </Td>
                        <Td
                          align="right"
                          className="bg-[var(--surface-spotlight)] border-l border-[var(--brand-100)]"
                          heightClass={rowHeight}
                          data-testid={`bundlerow-sellable-${testKey}`}
                        >
                          <span className="num">{(row.own.sellable ?? 0).toLocaleString()}</span>
                        </Td>
                        <Td
                          align="right"
                          className="bg-[var(--surface-spotlight)]"
                          heightClass={rowHeight}
                        >
                          <AllocationCell value={row.own.allocation_sellable ?? 0} />
                        </Td>
                        <Td className="bg-[var(--surface-spotlight)]" heightClass={rowHeight}>
                          {dash}
                        </Td>
                        <Td heightClass={rowHeight}>{dash}</Td>
                        <td
                          className={`${rowHeight} sticky right-0 z-[5] bg-[var(--row-bg,#fff)] group-hover:bg-[var(--row-hover)] shadow-[-10px_0_12px_-10px_rgba(16,24,40,0.16)] px-2 whitespace-nowrap`}
                        >
                          {bundleActions.length > 0 && <RowActionMenu actions={bundleActions} />}
                        </td>
                      </tr>
                    );
                  }
                  const key = rowKey(row);
                  const isSelected = selected.has(key);
                  const stockActions = [
                    ...(onEditSku && canWriteStock
                      ? [
                          {
                            key: "edit",
                            label: t("action.edit_product"),
                            icon: <Edit2 className="h-4 w-4" />,
                            onSelect: () => onEditSku(row.sku!, row.marketplace!),
                          },
                        ]
                      : []),
                    ...(onTransfer && canWriteStock
                      ? [
                          {
                            key: "transfer",
                            label: t("action.transfer"),
                            icon: <ArrowLeftRight className="h-4 w-4" />,
                            onSelect: () => onTransfer(row.sku!, row.marketplace!),
                          },
                        ]
                      : []),
                    ...(onAudit
                      ? [
                          {
                            key: "audit",
                            label: t("action.audit"),
                            icon: <ClipboardList className="h-4 w-4" />,
                            onSelect: () => onAudit(row.sku!, row.marketplace!),
                          },
                        ]
                      : []),
                    ...(canWriteStock
                      ? [
                          {
                            key: "calculate",
                            label: t("action.calculate"),
                            icon: <Calculator className="h-4 w-4" />,
                            onSelect: () =>
                              handleRecalculate(row.sku!, row.marketplace! as Marketplace),
                          },
                          {
                            key: "sync",
                            label: t("row.sync_now"),
                            icon: <RefreshCw className="h-4 w-4" />,
                            onSelect: () => handleSync(row.sku!, row.marketplace! as Marketplace),
                          },
                        ]
                      : []),
                    ...(onPushHistory
                      ? [
                          {
                            key: "push_history",
                            label: t("row.push_history"),
                            icon: <History className="h-4 w-4" />,
                            onSelect: () => onPushHistory(row.sku!, row.marketplace!),
                          },
                        ]
                      : []),
                    ...(onCheckSellername
                      ? [
                          {
                            key: "check_sellername",
                            label: t("action.check_sellername"),
                            icon: <Store className="h-4 w-4" />,
                            onSelect: () => onCheckSellername(row.sku!, row.marketplace!),
                          },
                        ]
                      : []),
                    ...(canWriteStock
                      ? [
                          {
                            key: "reset_defaults",
                            label: t("action.reset_defaults"),
                            icon: <RotateCcw className="h-4 w-4" />,
                            danger: true,
                            onSelect: () =>
                              setResetTarget({
                                sku: row.sku!,
                                marketplace: row.marketplace! as Marketplace,
                              }),
                          },
                        ]
                      : []),
                    ...(onDeleteSku && canWriteStock
                      ? [
                          {
                            key: "delete",
                            label: t("action.delete_sku"),
                            danger: true,
                            onSelect: () => onDeleteSku(row.sku!),
                          },
                        ]
                      : []),
                  ] satisfies RowAction[];
                  return (
                    <tr
                      key={key}
                      aria-selected={isSelected}
                      className="row-enter group transition-colors hover:bg-[var(--row-hover)]"
                      style={
                        {
                          animationDelay: `${Math.min(index, 10) * 30}ms`,
                          ...(isSelected
                            ? {
                                background: "var(--row-selected)",
                                "--row-bg": "var(--row-selected)",
                              }
                            : {}),
                        } as CSSProperties
                      }
                      data-testid="inventory-row"
                    >
                      {canWriteStock && (
                        <Td sticky left={0} width={48} heightClass={rowHeight}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(key)}
                            className="h-4 w-4 cursor-pointer rounded"
                            style={{ accentColor: "var(--brand-500)" }}
                            aria-label={`Select ${row.sku} ${row.marketplace}`}
                          />
                        </Td>
                      )}
                      <Td sticky left={skuLeft} width={260} heightClass={rowHeight}>
                        <div className="flex items-center gap-3">
                          <span
                            className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] text-xs font-bold text-white"
                            style={{
                              background: thumbGradient(row.sku ?? ""),
                              boxShadow: "var(--sh-xs)",
                            }}
                          >
                            {thumbInitial(row.product_name ?? row.sku ?? "")}
                          </span>
                          <span className="min-w-0">
                            <span
                              className="block max-w-[210px] truncate text-[13.5px] font-semibold text-[var(--text-primary)]"
                              title={row.product_name ?? row.sku}
                            >
                              {row.product_name ?? row.sku}
                            </span>
                            <span className="sku block text-[11.5px] text-[var(--text-subtle)]">
                              {row.sku}
                            </span>
                          </span>
                        </div>
                      </Td>
                      <Td sticky left={skuLeft + 260} width={132} heightClass={rowHeight}>
                        <MarketplaceChip
                          marketplace={row.marketplace as "shopee" | "lazada" | "tiktok"}
                        />
                      </Td>
                      <Td heightClass={rowHeight} data-testid={`row-type-${key}`}>
                        {t("type.single")}
                      </Td>
                      <Td align="right" heightClass={rowHeight}>
                        <span className="num">{row.on_hand?.toLocaleString() ?? 0}</span>
                      </Td>
                      <Td align="right" heightClass={rowHeight}>
                        <div className="flex items-center justify-end gap-1">
                          {row.sku && row.marketplace ? (
                            <InlineEditCell
                              value={String(row.fake ?? 0)}
                              onSave={handleFakeSave(row.sku!, row.marketplace!)}
                              validate={(v) => {
                                const n = parseInt(v, 10);
                                return !Number.isFinite(n) || n < 0 ? "Must be ≥ 0" : null;
                              }}
                              defaultValue={String(row.fake_default ?? 0)}
                              defaultLabel={t("action.set_default_aria", {
                                field: t("column.fake"),
                              })}
                              enableRemark
                              remarkLabel={t("remark.label")}
                              remarkPlaceholder={t("remark.placeholder")}
                              className="text-right"
                              readOnly={!canWriteStock}
                              data-testid={`inline-fake-${row.sku}-${row.marketplace}`}
                            />
                          ) : (
                            <span className="text-[var(--text-subtle)]">
                              {t("common:placeholder.empty")}
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td align="right" heightClass={rowHeight}>
                        {row.sku && row.marketplace ? (
                          <InlineEditCell
                            value={String(row.lock ?? 0)}
                            onSave={handleLockSave(row.sku!, row.marketplace!)}
                            validate={(v) => {
                              const n = parseInt(v, 10);
                              return !Number.isFinite(n) || n < 0 ? "Must be ≥ 0" : null;
                            }}
                            defaultValue={String(row.lock_default ?? 0)}
                            defaultLabel={t("action.set_default_aria", {
                              field: t("column.lock"),
                            })}
                            enableRemark
                            remarkLabel={t("remark.label")}
                            remarkPlaceholder={t("remark.placeholder")}
                            className="text-right"
                            readOnly={!canWriteStock}
                            data-testid={`inline-lock-${row.sku}-${row.marketplace}`}
                          />
                        ) : (
                          <span className="text-[var(--text-subtle)]">
                            {t("common:placeholder.empty")}
                          </span>
                        )}
                      </Td>
                      <Td align="right" heightClass={rowHeight}>
                        <span className="num">{row.reserve?.toLocaleString() ?? 0}</span>
                      </Td>
                      <Td align="right" heightClass={rowHeight}>
                        <span className="num">{row.bundle_reserve?.toLocaleString() ?? 0}</span>
                      </Td>
                      <Td align="right" heightClass={rowHeight}>
                        {row.sku && row.marketplace ? (
                          <InlineEditCell
                            value={row.multiplier ?? "1.000"}
                            displayValue={formatMultiplier(row.multiplier ?? "1.000")}
                            onSave={handleMultiplierSave(row.sku!, row.marketplace!)}
                            validate={(v) => {
                              const r = parseMultiplier(v);
                              return r.ok ? null : r.error;
                            }}
                            defaultValue={row.multiplier_default ?? "1.000"}
                            defaultLabel={t("action.set_default_aria", {
                              field: t("column.multiplier"),
                            })}
                            enableRemark
                            remarkLabel={t("remark.label")}
                            remarkPlaceholder={t("remark.placeholder")}
                            className="text-right"
                            readOnly={!canWriteStock}
                            data-testid={`inline-multiplier-${row.sku}-${row.marketplace}`}
                          />
                        ) : (
                          <span className="text-[var(--text-subtle)]">
                            {t("common:placeholder.empty")}
                          </span>
                        )}
                      </Td>
                      <Td
                        align="right"
                        className="bg-[var(--surface-spotlight)] border-l border-[var(--brand-100)]"
                        heightClass={rowHeight}
                      >
                        <span
                          className="num"
                          data-testid={`stock-sellable-${row.sku}-${row.marketplace}`}
                        >
                          {row.sellable?.toLocaleString() ?? 0}
                        </span>
                      </Td>
                      <Td
                        align="right"
                        className="bg-[var(--surface-spotlight)]"
                        heightClass={rowHeight}
                      >
                        <AllocationCell value={row.allocation_sellable ?? 0} />
                      </Td>
                      <Td className="bg-[var(--surface-spotlight)]" heightClass={rowHeight}>
                        {row.status_code ? <StockStatusBadge status={row.status_code} /> : null}
                      </Td>
                      <Td heightClass={rowHeight}>
                        <span
                          className="whitespace-nowrap text-[13px] text-[var(--text-subtle)] tabular-nums"
                          data-testid={`stock-synced-${row.sku}`}
                        >
                          <SyncedCell row={row} />
                        </span>
                      </Td>
                      <td
                        className={`${rowHeight} sticky right-0 z-[5] bg-[var(--row-bg,#fff)] group-hover:bg-[var(--row-hover)] shadow-[-10px_0_12px_-10px_rgba(16,24,40,0.16)] px-2 whitespace-nowrap`}
                      >
                        {stockActions.length > 0 && <RowActionMenu actions={stockActions} />}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {canWriteStock && selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 rounded-2xl px-4 py-2.5 pl-5 text-white shadow-[var(--sh-pop)]"
          style={{ background: "#131a29" }}
          data-testid="bulk-action-bar"
        >
          <span className="text-[13.5px] font-bold">
            {t("bulk.skus_selected", { count: selected.size })}
          </span>
          <span className="h-5 w-px bg-white/20" />
          <button
            type="button"
            onClick={() => {
              const targets = [...selected].map(parseRowKey);
              resetDefaults(targets, {
                onSuccess: () => {
                  toast.success(t("bulk.reset_done", { count: selected.size }));
                  setSelected(new Set());
                },
              });
            }}
            disabled={isResetting}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/10 px-3 text-[13px] font-semibold transition hover:bg-white/20 disabled:opacity-50"
          >
            <RotateCcw className="h-[15px] w-[15px]" aria-hidden />
            {t("toolbar.reset_defaults")}
          </button>
          <button
            type="button"
            onClick={() => {
              const targets = [...selected].map(parseRowKey);
              syncSelected(
                { targets },
                {
                  onSuccess: (res) => {
                    const failed = res.items.filter((i) => !i.ok).length;
                    if (failed === 0)
                      toast.success(t("bulk.sync_done", { count: res.items.length }));
                    else toast.error(t("bulk.sync_partial", { failed, count: res.items.length }));
                    setSelected(new Set());
                  },
                  onError: () => toast.error(t("bulk.sync_error")),
                },
              );
            }}
            disabled={isSyncing}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/10 px-3 text-[13px] font-semibold transition hover:bg-white/20 disabled:opacity-50"
          >
            <RefreshCw className="h-[15px] w-[15px]" aria-hidden />
            {t("bulk.sync")}
          </button>
          <button
            type="button"
            onClick={() => setBulkEditOpen(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[var(--brand-500)] px-3 text-[13px] font-semibold transition hover:bg-[#2f76ff]"
          >
            <Pencil className="h-[15px] w-[15px]" aria-hidden />
            {t("toolbar.bulk_edit")}
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            aria-label="Clear selection"
            className="grid h-8 w-8 place-items-center rounded-lg bg-white/10 transition hover:bg-white/20"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
      {bulkEditOpen && (
        <BulkEditModal
          rows={[...selected].map((key) => {
            const { sku, marketplace } = parseRowKey(key);
            return { sku, mkp: marketplace };
          })}
          onClose={() => setBulkEditOpen(false)}
          onApplied={() => setSelected(new Set())}
        />
      )}
      {resetTarget && (
        <ResetRowConfirm
          sku={resetTarget.sku}
          marketplace={resetTarget.marketplace}
          onClose={() => setResetTarget(null)}
        />
      )}
    </>
  );
}
